import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TableContainer,
  TableContent,
  TableHeaderCustomContent,
  INPUT_SIZES,
  Divider,
  TablePagination,
  preventDefaultBehavior,
  focusLastCell,
  usePreventTableScrollingRef,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  Switch,
  Checkbox,
  Dialog,
  TextRenderer,
  BUTTON_VARIANTS,
  SearchInput,
} from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import styled from 'styled-components';
import classNames from 'classnames';
import '../../styles/ApiFields.scss';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import {
  storeAllApiFields,
  selectedApiFields,
  selectedAllApiFields,
  setApiFieldSearchInput,
} from '../../../../../../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';
import { requiredApiFieldsForSpec } from '../../../../../../../constants/constants';
import { ApiFieldNameTooltip } from '../../../../../../UI/ApiFieldNameTooltip';

export const ApiFieldsSwitchRenderer = ({ data = {} }) => {
  const dispatch = useDispatch();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const { requestedAPIFields } = useSelector(
    (state) => state.ManageIntegrationsV2Slice
  );

  const { fieldName, required, index } = data;

  const StyledDialog = styled(Dialog)`
    max-width: 510px;
  `;
  const handleSwitchChange = useCallback((e, fieldName) => {
    const isSelected = e.target.checked;
    if (required && !isSelected) {
      setShowWarningDialog(true);
    } else {
      dispatch(selectedApiFields({ fieldName, isSelected }));
    }
  }, []);

  const handleClose = () => {
    setShowWarningDialog(false);
  };

  const handleContinue = () => {
    setShowWarningDialog(false);
    dispatch(selectedApiFields({ fieldName, isSelected: false }));
  };

  return (
    <>
      <Checkbox
        data-testid={`api-fields-switch-${index}`}
        key={index}
        checked={
          requestedAPIFields.length && requestedAPIFields.includes(fieldName)
            ? true
            : false
        }
        onChange={(e) => handleSwitchChange(e, fieldName)}
      />
      <ApiFieldNameTooltip {...data} />
      <StyledDialog
        isOpen={showWarningDialog}
        id="dialog-3"
        onClose={handleClose}
        title={'Warning'}
        buttonsProps={[
          {
            id: 'dialog-3-action-1',
            onClick: handleContinue,
            text: 'Continue Anyway',
            variant: BUTTON_VARIANTS.TEXT,
          },
        ]}
      >
        You have disabled a required field. This action might cause your app to
        no longer function as intended. Do you wish to continue?
      </StyledDialog>
    </>
  );
};

const ApiFieldsRenderer = ({ data = {} }) => {
  const { fieldName, required } = data;

  const prepareFieldName = required === true ? `${fieldName} *` : fieldName;

  return <div className="api-field-name">{prepareFieldName}</div>;
};

export const CustomHeaderCheckbox = (props) => {
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const { requestedAPIFields, apiFields, apiRequiredFields, apiFieldSearchInput } = state && state?.ManageIntegrationsV2Slice;
  const filteredRows = props?.api?.rowModel?.rowsToDisplay || [];
  const filteredFieldNames = filteredRows.map((row) => row.data.fieldName);

  const isAllSelected =
    apiFieldSearchInput && apiFieldSearchInput.length > 0
      ? filteredFieldNames.length > 0 &&
        filteredFieldNames.every((field) => requestedAPIFields.includes(field))
      : apiFields.length > 0 &&
        apiFields.every((field) => requestedAPIFields.includes(field));
  
  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      // Select all fields
      dispatch(
        selectedAllApiFields({
          fields:
            apiFieldSearchInput && apiFieldSearchInput.length > 0
              ? Array.from(
                  new Set([...requestedAPIFields, ...filteredFieldNames])
                )
              : apiFields,
          operation: 'addAllFields',
        })
      );
    } else {
      // Deselect all fields
      dispatch(
        selectedAllApiFields({
          fields:
            apiFieldSearchInput && apiFieldSearchInput.length > 0
              ? requestedAPIFields.filter(
                  (field) => !filteredFieldNames.includes(field)
                )
              : [],
          operation: 'removeAllFields',
        })
      );
    }
  };

  const StyledDialog = styled(Dialog)`
    max-width: 510px;
  `;

  const handleClose = () => {
    setShowWarningDialog(false);
  };

  const handleChange = (event) => {
    const isChecked = event.target.checked;
    if (
      isChecked === false &&
      filteredFieldNames.some((field) => apiRequiredFields.includes(field))
    ) {
      setShowWarningDialog(true);
    } else {
      handleSelectAll(isChecked);
    }
  };

  const handleContinue = () => {
    setShowWarningDialog(false);
    handleSelectAll(false);
  };


  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Checkbox
        checked={isAllSelected}
        onChange={handleChange}
        id="select-all-checkbox"
        name="select-all-checkbox"
        dataTestId="select-all-checkbox"
      />
      <StyledDialog
        isOpen={showWarningDialog}
        dataTestId="required-field-warning-dialog"
        id="required-field-warning-dialog"
        onClose={handleClose}
        title={'Warning'}
        buttonsProps={[
          {
            id: 'disable-continue-btn',
            dataTestId: 'disable-continue-btn',
            onClick: handleContinue,
            text: 'Continue',
            variant: BUTTON_VARIANTS.TEXT,
          },
        ]}
      >
        You have disabled all required fields. This action might cause your app
        to no longer function as intended. Do you wish to continue?
      </StyledDialog>
      <span>Fields</span>
    </div>
  );
};

export const ManageApiFieldsTable = ({ apiName, apiId }) => {
  const gridRef = useRef();
  const dispatch = useDispatch();

  const { apiFieldSelections } = useSelector(
    (state) => state.ConfigurationsSlice
  );

  const { requestedAPIFields, apiFieldSearchInput } = useSelector(
    (state) => state.ManageIntegrationsV2Slice
  );

  const [gridApi, setGridApi] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState();
  const [totalRows, setTotalRows] = useState();
  const [totalPages, setTotalPages] = useState();
  const tableContentRef = usePreventTableScrollingRef();
  const isAgGridReady = !!gridApi;
  const defaultPaginationPageSize = DEFAULT_ROWS_PER_PAGE_OPTIONS[0];
  const rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS;

  const frameworkComponentsExtended = {
    textRenderer: TextRenderer,
  };

  const componentMainClass = 'privacy_protection_table';
  const agGridThemeClass = 'ag-theme-balham';
  const tableClass = classNames(componentMainClass, agGridThemeClass);

  const columns = [
    {
      cellRendererFramework: ApiFieldsSwitchRenderer,
      field: 'fieldName',
      headerName: 'Fields',
      headerComponentFramework: CustomHeaderCheckbox,
      getQuickFilterText: params => {
        return params.data.fieldName || '';
      }
    },
  ];

  const handleSearchInputChange = (searchInput) => {
    dispatch(setApiFieldSearchInput(searchInput));
    if (gridApi) {
      gridApi.setQuickFilter(searchInput); // Refresh is needed for filtered cells highlighting to work properly

      gridApi.refreshCells({
        force: true,
      });
      const displayedRowsCount = gridApi.getDisplayedRowCount();
      setTotalRows(displayedRowsCount);

      if (searchInput && displayedRowsCount === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  };

  const handleTabbingFromPagination = (event) => {
    const hasRowsToDisplay = gridApi.rowModel.rowsToDisplay.length > 0;
    if (hasRowsToDisplay) {
      preventDefaultBehavior(event);
      focusLastCell(gridApi);
    }
  };

  const paginationPageSize = () => {
    if (isAgGridReady) {
      return gridApi.paginationGetPageSize();
    }

    if (
      defaultPaginationPageSize.value === DEFAULT_ROWS_PER_PAGE_OPTIONS[0].value
    ) {
      return rowsPerPageOptions[0].value;
    }

    if (
      rowsPerPageOptions.map(
        (rowPerPageOption) =>
          rowPerPageOption === defaultPaginationPageSize.value
      )
    ) {
      return defaultPaginationPageSize.value;
    }
  };

  const getPageSummaryText = (currentPage, totalPages) => {
    return `Page ${currentPage} of ${totalPages}`;
  };

  const getRowsSummaryText = (currentPage, pageSize, totalRows) => {
    if (totalRows === 0) {
      return `${totalRows}-${totalRows} of ${totalRows}`;
    }
    const rowsFrom = (currentPage - 1) * pageSize + 1;
    const rowsTo = currentPage * pageSize;
    return `${rowsFrom}-${
      rowsTo > totalRows ? totalRows : rowsTo
    } of ${totalRows}`;
  };

  const handlePaginationChanged = () => {
    if (!isAgGridReady) return;
    setCurrentPage(gridApi.paginationGetCurrentPage() + 1);
    setTotalPages(gridApi.paginationGetTotalPages());
    setTotalRows(gridApi.getDisplayedRowCount());
  };

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams.api);
    setCurrentPage(gridParams.api.paginationGetCurrentPage() + 1);
    setTotalPages(gridParams.api.paginationGetTotalPages());
    setTotalRows(gridParams.api.getDisplayedRowCount());
    gridParams.api.sizeColumnsToFit();
  };

  useEffect(() => {
    const selectedApi = apiFieldSelections[apiId.replace('api-v2-', '')];
    let apiFields = [];
    for (const fields of Object.values(
      selectedApi.apiFieldSelections.instances
    )) {
      fields.forEach((f) => {
        apiFields.push({
          name: f.name,
          required: f.required,
          description: f.description,
          example: f.example,
        });
      });
    }
    const uniqueApiFields = Array.from(apiFields).filter((field, index) => {
      return apiFields.findIndex((f) => f.name === field.name) === index;
    });

    if (!requestedAPIFields?.length) {
      dispatch(
        storeAllApiFields({
          apiFields: uniqueApiFields,
          manualSelectionFields: selectedApi.manualSelectionFields || [],
        }),
      );
    }

    let mapApiFields = uniqueApiFields.map((f) => {
      return {
        fieldName: f.name,
        required: f.required,
        description: f.description,
        example: f.example,
      };
    });
    // mask required fields in UI
    mapApiFields = mapApiFields.filter(
      (f) => !requiredApiFieldsForSpec.has(f.fieldName)
    );

    setRowData(mapApiFields);
  }, []);

  const paginationProps = {
    ariaLabel: 'Pagination',
    buttonLabels: {
      goToFirst: 'Go to first',
      goToLast: 'Go to last',
      goToNext: 'Go to next',
      goToPrevious: 'Go to previous',
    },
    className: '',
    dataTestId: `purchase-history-table-pagination`,
    defaultPaginationPageSize: DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
    gridApi,
    hideBorder: false,
    onTabbingIntoTable: handleTabbingFromPagination,
    pageSizeDropdownId: `purchase-history-page-size-dropdown-table`,
    pageSummaryLabelId: `purchase-history-pagination-summary-label-table`,
    pageSummaryText: getPageSummaryText(
      totalPages === 0 ? 0 : currentPage,
      totalPages
    ),
    rowsPerPageLabel: 'Rows per page:',
    rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    rowsSummaryText: getRowsSummaryText(
      currentPage,
      paginationPageSize(),
      totalRows
    ),
    showGoToFirstLastPage: true,
  };

  return (
    <TableContainer className={tableClass}>
      <TableHeaderCustomContent
        className="custom-header"
        customHeaderContent={
          <div className="api-fields-header-container">
            <div className="api-fields-header-text-container">
              <h3 className="api-fields-header-text">
                Configure fields for {apiName}
              </h3>
              <span>
                All fields with * are required for optimal App function
              </span>
            </div>
            <SearchInput
              clearButtonLabel="clearButtonLabel"
              id="search-input-expanded"
              isAlwaysExpanded
              name="search-input-expanded"
              onChange={handleSearchInputChange}
              placeholder="Search fields"
              size={INPUT_SIZES.STANDARD}
              value={apiFieldSearchInput}
            />
          </div>
        }
      />
      <TableContent ref={tableContentRef} className="manage-fields-table">
        <Divider></Divider>
        <AgGridReact
          columnDefs={columns}
          suppressRowHoverHighlight={true}
          rowData={rowData}
          suppressPaginationPanel
          frameworkComponents={frameworkComponentsExtended}
          onPaginationChanged={handlePaginationChanged}
          pagination={true}
          paginationPageSize={paginationPageSize()}
          paginationAutoPageSize={false}
          onGridReady={handleGridReady}
          ref={gridRef}
          rowHeight={32}
          domLayout="autoHeight"
          disableStaticMarkup={true}
        />
      </TableContent>
      {isAgGridReady && (
        <TablePagination
          defaultPaginationPageSize={defaultPaginationPageSize}
          {...paginationProps}
        />
      )}
    </TableContainer>
  );
};
